import { Fragment, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AdminAnalyticsReport, AttributionRow, AttributionSummary, ExecutiveInsight, JourneySummary, WorkedAttributionRow, WorkedLeadOutcomes, WorkedLeadOutcome } from '../types';
import { sourceLabel } from '../utils/source-label';

// ============================================================================
// DESIGN SYSTEM — Skydash-inspired palette + semantic status colors
// ============================================================================

export const STATUS_COLORS: Record<string, string> = {
  unassigned: '#f97316',
  new: '#64748b',
  pending: '#64748b',
  contacted: '#2563eb',
  follow_up: '#f59e0b',
  qualified: '#8b5cf6',
  won: '#10b981',
  lost: '#e11d48',
  not_interested: '#e11d48',
  spam: '#94a3b8',
};

export const STATUS_LABELS: Record<string, string> = {
  unassigned: 'Unassigned',
  new: 'New',
  pending: 'Pending',
  contacted: 'Contacted',
  follow_up: 'Follow-up',
  qualified: 'Qualified',
  won: 'Won',
  lost: 'Lost',
  not_interested: 'Not Interested',
  spam: 'Spam',
};

// Skydash-inspired palette
export const PALETTE = {
  pageBg: '#F5F7FF',
  indigo: '#4B49AC',
  blue: '#7DA0FA',
  softBlue: '#98BDFF',
  violet: '#7978E9',
  textDark: '#17213A',
  textSecondary: '#8A9AB5',
  cardBg: '#FFFFFF',
  border: '#E8EBFB',
  tintLight: '#F8F9FF',
  tintBlue: '#F3F6FF',
};

// Statuses with configured reason options in the CRM (contacted, qualified, won, lost).
// Follow-up has NO configured reasons — must not show fake reason data.
export const STATUSES_WITH_REASONS = ['contacted', 'qualified', 'won', 'lost'];

// Display order for outcomes in the flow visual.
const OUTCOME_ORDER = ['contacted', 'follow_up', 'qualified', 'won', 'lost', 'new'];

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
}

function statusColor(status: string): string {
  return STATUS_COLORS[status] ?? '#94a3b8';
}

export function num(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function pct(count: number, total: number): string {
  if (total <= 0) return '0%';
  return `${((count / total) * 100).toFixed(1)}%`;
}

export function pctNum(count: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((count / total) * 1000) / 10;
}

// Generate initials from a name for avatar display.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic soft avatar background color from a name.
function avatarBg(name: string): string {
  const colors = ['#E0E7FF', '#DBEAFE', '#FAE8FF', '#DCFCE7', '#FEF3C7', '#FFE4E6', '#E0F2FE', '#EDE9FE'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0x7fffffff;
  return colors[hash % colors.length];
}

function avatarFg(name: string): string {
  const colors = ['#4B49AC', '#2563eb', '#a21caf', '#15803d', '#b45309', '#be123c', '#0369a1', '#7c3aed'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0x7fffffff;
  return colors[hash % colors.length];
}

// ============================================================================
// TYPES — all v2 fields optional for old-snapshot safety
// ============================================================================

export type StatusReasonEntry = { status: string; total: number; reasons: Array<{ reason: string; count: number; percentage: number }> };
export type PipelineEntry = { status: string; count: number };
type WorkTypeEntry = { type: string; label: string; attempts: number; unique: number };
type AgentCallEntry = { label: string; attempts: number; unique: number; connected: number; not_connected: number; no_patient_outcome?: number };
type AgentWorkMixEntry = { label: string; new_lead: number; old_assigned_today: number; follow_up: number };
type AgentOutcomeEntry = { label: string; outcomes: Record<string, number> };

type EodSummary = {
  new_today?: number;
  unassigned_today?: number;
  total_unassigned?: number;
  assigned_today?: number;
  total_call_attempts?: number;
  unique_leads_called?: number;
  connected_calls?: number;
  not_connected_calls?: number;
  // Schema 12+: patients whose attempts were only technical failures or unresolved.
  no_patient_outcome?: number;
  technical_failure_attempts?: number;
  new_lead_call_attempts?: number;
  new_lead_call_unique?: number;
  old_assigned_today_attempts?: number;
  old_assigned_today_unique?: number;
  follow_up_attempts?: number;
  follow_up_unique?: number;
  lost_leads?: number;
};

type EodChartData = {
  pipeline?: PipelineEntry[];
  pipeline_total?: number;
  status_reason_breakdown?: StatusReasonEntry[];
  lost_reasons?: Array<{ reason: string; count: number }>;
  lost_total?: number;
  work_type?: WorkTypeEntry[];
  connection_split?: Array<{ label: string; value: number }>;
  calls_by_telecaller?: AgentCallEntry[];
  agent_work_mix?: AgentWorkMixEntry[];
  agent_outcomes?: AgentOutcomeEntry[];
  journey_summary?: JourneySummary;
  attribution_summary?: AttributionSummary;
  executive_insights?: ExecutiveInsight[];
};

type AgentRow = {
  telecaller_id: string;
  telecaller_name: string;
  total_call_attempts: number;
  unique_leads_called?: number;
  connected_calls: number;
  not_connected_calls: number;
  no_patient_outcome?: number;
  connection_rate?: number;
  new_lead_call_attempts?: number;
  new_lead_unique?: number;
  old_assigned_today_attempts?: number;
  old_assigned_today_unique?: number;
  follow_up_call_attempts?: number;
  follow_up_call_unique?: number;
  assigned_today?: number;
  outcomes?: Record<string, number>;
};

// ============================================================================
// CHART TOOLTIP — styled to match the design system
// ============================================================================

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-[#E8EBFB] bg-white px-3 py-2 shadow-[0_4px_20px_rgba(75,73,172,0.12)]">
      {label && <div className="mb-1 text-xs font-semibold text-[#17213A]">{label}</div>}
      {payload.map((entry: any, i: number) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
          <span className="text-[#8A9AB5]">{entry.name}:</span>
          <span className="font-semibold text-[#17213A]">{num(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// PRIMITIVES — Skydash-style elevated cards
// ============================================================================

export function Card({ children, className = '', padding = 'p-6', tint }: { children: ReactNode; className?: string; padding?: string; tint?: boolean }) {
  return (
    <div
      className={`rounded-2xl border border-[#E8EBFB] ${tint ? 'bg-[#F8F9FF]' : 'bg-white'} ${padding} shadow-[0_4px_20px_rgba(75,73,172,0.05)] ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionLabel({ title, subtitle, right }: { title: string; subtitle?: string; right?: ReactNode }) {
  return (
    <div className="mb-5 flex items-end justify-between gap-3">
      <div>
        <h3 className="text-[17px] font-semibold tracking-tight text-[#17213A]">{title}</h3>
        {subtitle && <p className="mt-0.5 text-[13px] text-[#8A9AB5]">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return <div className="flex h-32 items-center justify-center rounded-xl bg-[#F8F9FF] text-sm text-[#8A9AB5]">{message}</div>;
}

// ============================================================================
// KPI AREA — hero metric + supporting cards
// ============================================================================

function HeroKpi({ label, value, sub, barValue, barColor }: { label: string; value: number; sub: string; barValue: number; barColor: string }) {
  return (
    <div className="flex flex-col justify-between rounded-2xl border border-[#E8EBFB] bg-gradient-to-br from-[#4B49AC] to-[#7978E9] p-6 text-white shadow-[0_8px_30px_rgba(75,73,172,0.2)]">
      <div className="text-[13px] font-medium uppercase tracking-wide text-white/70">{label}</div>
      <div className="mt-3 text-[44px] font-bold leading-none tracking-tight">{value}</div>
      <div className="mt-2 text-[13px] text-white/80">{sub}</div>
      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-white/20">
        <div className="h-2 rounded-full bg-white transition-all duration-300" style={{ width: `${Math.min(barValue, 100)}%` }} />
      </div>
    </div>
  );
}

export function SupportKpi({ label, value, sub, accent, hint }: { label: string; value: string | number; sub: string; accent: string; hint?: string }) {
  return (
    <div title={hint} className="rounded-2xl border border-[#E8EBFB] bg-white p-5 shadow-[0_4px_20px_rgba(75,73,172,0.05)] transition-shadow duration-200 hover:shadow-[0_8px_30px_rgba(75,73,172,0.1)]">
      <div className="text-[12px] font-medium text-[#8A9AB5]">{label}</div>
      <div className="mt-2 text-[28px] font-bold leading-none tracking-tight" style={{ color: accent }}>{value}</div>
      <div className="mt-1.5 text-[12px] text-[#8A9AB5]">{sub}</div>
    </div>
  );
}

// ============================================================================
// LEAD JOURNEY — main centerpiece: proportional flow bars
// ============================================================================

export function LeadJourney({
  pipeline,
  pipelineTotal,
  selected,
  onSelect,
}: {
  pipeline: PipelineEntry[];
  pipelineTotal: number;
  selected: string;
  onSelect: (status: string) => void;
}) {
  const outcomes = useMemo(() => {
    const present = pipeline.filter((p) => p.status !== 'unassigned' && p.count > 0);
    return OUTCOME_ORDER
      .filter((s) => present.some((p) => p.status === s))
      .map((s) => present.find((p) => p.status === s)!)
      .concat(present.filter((p) => !OUTCOME_ORDER.includes(p.status)));
  }, [pipeline]);

  if (pipelineTotal <= 0 || outcomes.length === 0) {
    return <EmptyState message="No lead records were created in this window." />;
  }

  const maxCount = Math.max(...outcomes.map((o) => o.count), 1);

  return (
    <div>
      {/* Total headline */}
      <div className="mb-5 flex items-baseline gap-3">
        <span className="text-[40px] font-bold leading-none tracking-tight text-[#17213A]">{pipelineTotal}</span>
        <span className="text-[13px] font-medium text-[#8A9AB5]" title="Includes every inbound call ring with a caller number, answered or not">Lead Records Created</span>
      </div>

      {/* Source bar — full width */}
      <div className="mb-1 flex items-center gap-3">
        <div className="w-24 shrink-0 text-right text-[12px] font-medium text-[#8A9AB5]">All Leads</div>
        <div className="flex-1">
          <div className="flex h-7 w-full items-center rounded-lg bg-[#17213A] px-3">
            <span className="text-[12px] font-semibold text-white">{pipelineTotal}</span>
          </div>
        </div>
      </div>

      {/* Connector */}
      <div className="mb-2 ml-[calc(6rem+0.75rem)] h-5 w-px bg-[#E8EBFB]" />

      {/* Outcome rows — proportional, clickable */}
      <div className="flex flex-col gap-2">
        {outcomes.map((o) => {
          const widthPct = Math.max((o.count / maxCount) * 100, 10);
          const isSelected = selected === o.status;
          return (
            <button
              key={o.status}
              onClick={() => onSelect(o.status)}
              data-export-tab="status"
              data-export-value={o.status}
              className={`group flex items-center gap-3 rounded-lg px-2 py-1 transition-all duration-200 ${isSelected ? 'bg-[#F3F6FF] ring-1 ring-[#7DA0FA]/40' : 'hover:bg-[#F8F9FF]'}`}
            >
              <div className="w-24 shrink-0 text-right text-[12px] font-medium text-[#17213A]">
                {statusLabel(o.status)}
              </div>
              <div className="flex-1">
                <div
                  className="flex h-8 items-center rounded-lg transition-all duration-200"
                  style={{ width: `${widthPct}%`, backgroundColor: statusColor(o.status), minWidth: '56px' }}
                >
                  <span className="px-3 text-[12px] font-semibold text-white">{o.count}</span>
                </div>
              </div>
              <div className="flex w-16 shrink-0 items-center justify-end gap-1.5">
                <span className="text-[14px] font-bold text-[#17213A]">{o.count}</span>
                <span className="text-[11px] text-[#8A9AB5]">{pct(o.count, pipelineTotal)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// REASON EXPLORER — selected status → reason breakdown
// ============================================================================

function deriveInsight(
  status: string,
  count: number,
  pipelineTotal: number,
  reasons: Array<{ reason: string; count: number; percentage: number }>,
): string | null {
  if (count <= 0 || reasons.length === 0) return null;
  const top = reasons[0];
  if (top.count <= 0) return null;

  const statusName = statusLabel(status).toLowerCase();
  const topPct = top.percentage;

  if (status === 'lost') {
    return `Out of ${count} lost leads, ${top.count} (${topPct}%) were lost due to "${top.reason}".`;
  }
  if (status === 'won') {
    return `${top.count} of ${count} won leads (${topPct}%) were marked "${top.reason}".`;
  }
  if (status === 'contacted') {
    return `${top.count} of ${count} contacted leads (${topPct}%) — "${top.reason}".`;
  }
  if (status === 'qualified') {
    return `${top.count} of ${count} qualified leads (${topPct}%) — "${top.reason}".`;
  }
  return `${top.count} of ${count} ${statusName} leads (${topPct}%) — "${top.reason}".`;
}

export function ReasonExplorer({
  selectedStatus,
  pipeline,
  pipelineTotal,
  statusReasonBreakdown,
}: {
  selectedStatus: string;
  pipeline: PipelineEntry[];
  pipelineTotal: number;
  statusReasonBreakdown: StatusReasonEntry[];
}) {
  const outcome = pipeline.find((p) => p.status === selectedStatus);
  const count = outcome?.count ?? 0;
  const reasons = statusReasonBreakdown.find((s) => s.status === selectedStatus)?.reasons ?? [];
  const supportsReasons = STATUSES_WITH_REASONS.includes(selectedStatus);
  const maxReason = reasons.length > 0 ? Math.max(...reasons.map((r) => r.count), 1) : 1;
  const insight = deriveInsight(selectedStatus, count, pipelineTotal, reasons);

  if (count <= 0) {
    return <EmptyState message={`No leads are currently in ${statusLabel(selectedStatus)} status.`} />;
  }

  return (
    <div className="rounded-2xl bg-[#F8F9FF] p-5">
      {/* Header: status name + count + percentage */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="h-3.5 w-3.5 rounded" style={{ backgroundColor: statusColor(selectedStatus) }} />
          <span className="text-[18px] font-bold tracking-tight text-[#17213A]">{statusLabel(selectedStatus)}</span>
          <span className="text-[13px] font-medium text-[#8A9AB5]">
            {count} leads · {pct(count, pipelineTotal)}
          </span>
        </div>
        {supportsReasons && reasons.length > 0 && (
          <div className="text-right">
            <div className="text-[11px] font-medium text-[#8A9AB5]">Top Reason</div>
            <div className="mt-0.5 text-[14px] font-semibold text-[#17213A]">{reasons[0].reason}</div>
          </div>
        )}
      </div>

      {/* Reason bars or clean message */}
      {supportsReasons ? (
        reasons.length > 0 ? (
          <div className="space-y-3">
            {reasons.map((r) => (
              <div key={r.reason} className="group">
                <div className="mb-1 flex items-center justify-between text-[13px]">
                  <span className="truncate font-medium text-[#17213A]" title={r.reason}>{r.reason}</span>
                  <span className="ml-3 shrink-0 font-semibold text-[#17213A]">
                    {r.count} <span className="font-normal text-[#8A9AB5]">· {r.percentage}%</span>
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-white">
                  <div
                    className="h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(r.count / maxReason) * 100}%`, backgroundColor: statusColor(selectedStatus) }}
                  />
                </div>
              </div>
            ))}

            {/* Derived insight */}
            {insight && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#E8EBFB] bg-white px-4 py-3">
                <span className="mt-0.5 text-sm">💡</span>
                <span className="text-[13px] text-[#17213A]">{insight}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl bg-white py-8 text-center">
            <div className="text-[14px] font-medium text-[#17213A]">No reasons recorded</div>
            <div className="mt-1 text-[12px] text-[#8A9AB5]">
              {count} {statusLabel(selectedStatus).toLowerCase()} leads have no reason data. This may indicate agents are not recording reasons during status changes.
            </div>
          </div>
        )
      ) : (
        <div className="rounded-xl bg-white py-8 text-center">
          <div className="text-[14px] font-medium text-[#17213A]">Reason analysis is not collected for {statusLabel(selectedStatus)}</div>
          <div className="mt-1 text-[12px] text-[#8A9AB5]">
            The CRM does not configure reason options for this status.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CALLING PERFORMANCE — donut + work mix bars
// ============================================================================

function CallingPerformance({
  totalCalls,
  uniqueCalled,
  connected,
  notConnected,
  noPatientOutcome,
  workType,
}: {
  totalCalls: number;
  uniqueCalled: number;
  connected: number;
  notConnected: number;
  noPatientOutcome?: number;
  workType: WorkTypeEntry[];
}) {
  const connRate = uniqueCalled > 0 ? Math.round((connected / uniqueCalled) * 1000) / 10 : 0;
  const total = connected + notConnected + (noPatientOutcome ?? 0);
  const callData = [
    { name: 'Connected', value: connected, color: PALETTE.indigo },
    { name: 'No answer', value: notConnected, color: STATUS_COLORS.lost },
    { name: 'No patient outcome', value: noPatientOutcome ?? 0, color: STATUS_COLORS.spam },
  ];
  const maxWorkAttempts = workType.length > 0 ? Math.max(...workType.map((w) => w.attempts), 1) : 1;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
      {/* Left: donut + supporting metrics */}
      <div className="flex items-center gap-5">
        {total > 0 && (
          <div className="relative shrink-0">
            <ResponsiveContainer width={150} height={150}>
              <PieChart>
                <Pie data={callData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={65} paddingAngle={3}>
                  {callData.map((e) => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[24px] font-bold text-[#17213A]">{connRate}%</span>
              <span className="text-[11px] text-[#8A9AB5]">conn. rate</span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Metric label="Call Attempts" value={totalCalls} hint="Inbound and outbound CRM-visible attempts, including technical failures" />
          <Metric label="Unique Patients" value={uniqueCalled} />
          <Metric label="Patients Connected" value={connected} color={PALETTE.indigo} hint="At least one answered call" />
          <Metric label="Patients No Answer" value={notConnected} color={STATUS_COLORS.lost} hint="Patient-side no answer, never connected. Technical failures are not counted here." />
          {noPatientOutcome !== undefined && (
            <Metric label="No Patient Outcome" value={noPatientOutcome} color={STATUS_COLORS.spam} hint="Only technical failures (Vobiz/network) or still-unresolved attempts" />
          )}
        </div>
      </div>

      {/* Right: work mix with bars */}
      <div>
        <div className="mb-3 text-[13px] font-semibold text-[#17213A]">Work Mix</div>
        {workType.length > 0 && workType.some((w) => w.attempts > 0) ? (
          <div className="space-y-3">
            {workType.map((w) => (
              <div key={w.type}>
                <div className="mb-1 flex items-center justify-between text-[13px]">
                  <span className="font-medium text-[#17213A]">{w.label}</span>
                  <span className="text-[12px] text-[#8A9AB5]">{w.attempts} attempts · {w.unique} unique</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-[#F3F6FF]">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(w.attempts / maxWorkAttempts) * 100}%`, backgroundColor: PALETTE.blue }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[13px] text-[#8A9AB5]">No calling activity today.</div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, color, hint }: { label: string; value: number; color?: string; hint?: string }) {
  return (
    <div title={hint}>
      <div className="text-[12px] font-medium text-[#8A9AB5]">{label}</div>
      <div className="mt-0.5 text-[22px] font-bold" style={{ color: color ?? PALETTE.textDark }}>{value}</div>
    </div>
  );
}

// ============================================================================
// AGENT PERFORMANCE — segmented control + comparison chart
// ============================================================================

type AgentMetric = 'calls' | 'connected' | 'work_mix';

function AgentComparisonChart({
  callsByAgent,
  agentWorkMix,
}: {
  callsByAgent: AgentCallEntry[];
  agentWorkMix: AgentWorkMixEntry[];
}) {
  const [metric, setMetric] = useState<AgentMetric>('calls');

  return (
    <div data-export-tabs="agent-metric">
      <div className="mb-4 inline-flex rounded-lg border border-[#E8EBFB] bg-[#F8F9FF] p-1">
        {([
          ['calls', 'Call Attempts'],
          ['connected', 'Patients Connected / Not'],
          ['work_mix', 'Work Mix'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setMetric(key)}
            data-export-tab="agent-metric"
            data-export-value={key}
            className={`rounded-md px-3.5 py-1.5 text-[12px] font-medium transition-all duration-200 ${
              metric === key ? 'bg-[#4B49AC] text-white shadow-sm' : 'text-[#8A9AB5] hover:text-[#17213A]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {(['calls', 'connected', 'work_mix'] as const).map((key) => (
        <div key={key} data-export-panel="agent-metric" data-export-value={key} hidden={metric !== key}>
          <AgentChart metric={key} callsByAgent={callsByAgent} agentWorkMix={agentWorkMix} />
        </div>
      ))}
    </div>
  );
}

function AgentChart({
  metric,
  callsByAgent,
  agentWorkMix,
}: {
  metric: AgentMetric;
  callsByAgent: AgentCallEntry[];
  agentWorkMix: AgentWorkMixEntry[];
}) {

  const data = useMemo(() => {
    if (metric === 'calls') {
      return callsByAgent.filter((r) => r.attempts > 0).sort((a, b) => b.attempts - a.attempts);
    }
    if (metric === 'connected') {
      return callsByAgent.filter((r) => r.attempts > 0).sort((a, b) => (b.connected + b.not_connected) - (a.connected + a.not_connected));
    }
    return agentWorkMix.filter((r) => r.new_lead + r.old_assigned_today + r.follow_up > 0).sort((a, b) => (b.new_lead + b.old_assigned_today + b.follow_up) - (a.new_lead + a.old_assigned_today + a.follow_up));
  }, [metric, callsByAgent, agentWorkMix]);

  if (data.length === 0) {
    return <EmptyState message="No agent activity to compare." />;
  }

  const height = Math.max(140, data.length * 34 + 30);

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data as any[]} layout="vertical" margin={{ left: 8, right: 16, top: 0, bottom: 0 }}>
          <XAxis type="number" tick={{ fontSize: 11, fill: '#8A9AB5' }} axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fontSize: 11, fill: '#17213A' }}
            width={120}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: string) => (v.length > 16 ? v.slice(0, 15) + '…' : v)}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F8F9FF' }} />
          {metric === 'calls' && (
            <Bar dataKey="attempts" name="Call Attempts" fill={PALETTE.indigo} radius={[0, 4, 4, 0]} barSize={20} />
          )}
          {metric === 'connected' && (
            <>
              <Bar dataKey="connected" name="Connected" stackId="a" fill={PALETTE.indigo} radius={[0, 4, 4, 0]} barSize={20} />
              <Bar dataKey="not_connected" name="No answer" stackId="a" fill={STATUS_COLORS.lost} barSize={20} />
              <Bar dataKey="no_patient_outcome" name="No patient outcome" stackId="a" fill={STATUS_COLORS.spam} radius={[0, 4, 4, 0]} barSize={20} />
            </>
          )}
          {metric === 'work_mix' && (
            <>
              <Bar dataKey="new_lead" name="New Leads" stackId="a" fill={STATUS_COLORS.new} radius={[0, 4, 4, 0]} barSize={20} />
              <Bar dataKey="old_assigned_today" name="Old Assigned" stackId="a" fill={STATUS_COLORS.qualified} barSize={20} />
              <Bar dataKey="follow_up" name="Follow-ups" stackId="a" fill={STATUS_COLORS.follow_up} radius={[0, 4, 4, 0]} barSize={20} />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================================
// AGENT TABLE — polished analytics table with avatars + progress bars
// ============================================================================

function AgentTable({ rows, outcomes }: { rows: AgentRow[]; outcomes: AgentOutcomeEntry[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const outcomeByLabel = useMemo(() => {
    const m = new Map<string, Record<string, number>>();
    for (const o of outcomes) m.set(o.label, o.outcomes ?? {});
    return m;
  }, [outcomes]);

  return (
    <div className="overflow-x-auto rounded-xl border border-[#E8EBFB]">
      <table className="min-w-[1040px] w-full text-left text-[13px]">
        <thead className="bg-[#F8F9FF] text-[#8A9AB5]">
          <tr>
            {([
              ['Agent', ''],
              ['Attempts', 'Call attempts, including technical failures'],
              ['Unique Patients', ''],
              ['Connected', 'Unique patients with an answered call'],
              ['No Answer', 'Unique patients who did not answer and never connected'],
              ['No Outcome', 'Unique patients whose attempts were only technical failures or unresolved'],
              ['Conn. Rate', 'Connected ÷ unique patients'],
              ['New Leads', 'Attempts'],
              ['Old Assigned', 'Attempts'],
              ['Follow-ups', 'Attempts'],
              ['Assigned Today', ''],
            ] as const).map(([h, hint], i) => (
              <th key={h} title={hint || undefined} className={`px-4 py-3 text-[12px] font-semibold ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const isOpen = expanded === row.telecaller_id;
            const oc = outcomeByLabel.get(row.telecaller_name) ?? row.outcomes ?? {};
            const ocEntries = Object.entries(oc).sort((a, b) => num(b[1]) - num(a[1]));
            const connRateVal = num(row.connection_rate);
            return (
              <Fragment key={row.telecaller_id}>
                <tr
                  className={`cursor-pointer border-t border-[#F3F6FF] transition-colors duration-200 hover:bg-[#F8F9FF] ${idx % 2 === 1 ? 'bg-[#FBFCFF]' : 'bg-white'}`}
                  onClick={() => setExpanded(isOpen ? null : row.telecaller_id)}
                >
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2.5">
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                        style={{ backgroundColor: avatarBg(row.telecaller_name), color: avatarFg(row.telecaller_name) }}
                      >
                        {initials(row.telecaller_name)}
                      </span>
                      <span className="truncate font-semibold text-[#17213A]" title={row.telecaller_name}>{row.telecaller_name}</span>
                      <span className="text-[#8A9AB5]">{isOpen ? '▾' : '▸'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-[#17213A]">{num(row.total_call_attempts)}</td>
                  <td className="px-4 py-3 text-right text-[#17213A]">{num(row.unique_leads_called)}</td>
                  <td className="px-4 py-3 text-right font-semibold" style={{ color: PALETTE.indigo }}>{num(row.connected_calls)}</td>
                  <td className="px-4 py-3 text-right font-semibold" style={{ color: STATUS_COLORS.lost }}>{num(row.not_connected_calls)}</td>
                  <td className="px-4 py-3 text-right text-[#17213A]">{row.no_patient_outcome ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[12px] font-medium text-[#17213A]">{connRateVal}%</span>
                      <div className="h-1.5 w-12 overflow-hidden rounded-full bg-[#F3F6FF]">
                        <div className="h-1.5 rounded-full" style={{ width: `${Math.min(connRateVal, 100)}%`, backgroundColor: PALETTE.indigo }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-[#17213A]">{num(row.new_lead_call_attempts)}</td>
                  <td className="px-4 py-3 text-right text-[#17213A]">{num(row.old_assigned_today_attempts)}</td>
                  <td className="px-4 py-3 text-right text-[#17213A]">{num(row.follow_up_call_attempts)}</td>
                  <td className="px-4 py-3 text-right text-[#17213A]">{num(row.assigned_today)}</td>
                </tr>
                {isOpen && (
                  <tr className="border-t border-[#F3F6FF] bg-[#F8F9FF]">
                    <td colSpan={11} className="px-5 py-3">
                      <div className="mb-2 text-[11px] font-semibold text-[#8A9AB5]">
                        Current outcomes of leads assigned today
                      </div>
                      {ocEntries.length ? (
                        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[13px]">
                          {ocEntries.map(([status, count]) => (
                            <div key={status} className="flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: statusColor(status) }} />
                              <span className="text-[#17213A]">{statusLabel(status)}</span>
                              <span className="font-semibold text-[#17213A]">{num(count)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[12px] text-[#8A9AB5]">No leads assigned to this agent today.</div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {!rows.length && (
            <tr>
              <td colSpan={11} className="px-4 py-8 text-center text-[#8A9AB5]">No active telecallers.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// JOURNEY FUNNEL — Total → Assigned → Called → Connected → Qualified → Won
// ============================================================================

function JourneyFunnelBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const widthPct = max > 0 ? Math.max((value / max) * 100, 2) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 shrink-0 text-right text-[13px] font-medium text-[#17213A]">{label}</div>
      <div className="relative h-7 flex-1 overflow-hidden rounded-md bg-slate-100">
        <div className="flex h-full items-center rounded-md px-2 text-[11px] font-semibold text-white" style={{ width: `${widthPct}%`, backgroundColor: color, minWidth: '28px' }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export function JourneyFunnel({ journey }: { journey: JourneySummary }) {
  const max = Math.max(journey.total_leads, 1);
  // Snapshots before schema 12 lack positive/registered/no-outcome; show only what they measured.
  const stages: Array<{ label: string; value: number; color: string; hint?: string }> = [
    { label: 'Total', value: journey.total_leads, color: PALETTE.textSecondary, hint: 'Unique people among lead records created in the window, including inbound call rings' },
    { label: 'Assigned', value: journey.assigned, color: PALETTE.indigo },
    { label: 'Called', value: journey.called, color: PALETTE.blue, hint: 'At least one CRM-visible call attempt, inbound or outbound' },
    { label: 'Connected', value: journey.connected, color: PALETTE.violet, hint: 'At least one answered call' },
    { label: 'Marked Qualified', value: journey.qualified, color: STATUS_COLORS.qualified, hint: 'Moved to Qualified status in the window, whatever the qualification outcome (declined included)' },
    ...(journey.qualified_positive !== undefined ? [{ label: 'Positive', value: journey.qualified_positive, color: STATUS_COLORS.qualified, hint: 'Human-confirmed: qualification outcome recorded as positive' }] : []),
    { label: 'Won (status)', value: journey.won, color: STATUS_COLORS.won, hint: 'Won status in the window; includes legacy wins without registration evidence' },
    ...(journey.registered !== undefined ? [{ label: 'Registered', value: journey.registered, color: STATUS_COLORS.won, hint: 'Registration recorded with a reference. Not an OPD consultation or payment.' }] : []),
  ];
  return (
    <div className="space-y-2">
      {stages.map((s) => (
        <div key={s.label} title={s.hint}>
          <JourneyFunnelBar label={s.label} value={s.value} max={max} color={s.color} />
        </div>
      ))}
      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-[12px] sm:grid-cols-3">
        <div><span className="text-slate-400">Unassigned:</span> <span className="font-semibold text-slate-700">{journey.unassigned}</span></div>
        <div><span className="text-slate-400">Not Called:</span> <span className="font-semibold text-slate-700">{journey.not_called}</span></div>
        {journey.no_patient_outcome !== undefined ? (
          <>
            <div title="Patient-side no answer, never connected"><span className="text-slate-400">Patient No Answer:</span> <span className="font-semibold text-slate-700">{journey.not_connected}</span></div>
            <div title="Only technical failures (Vobiz/network) or unresolved attempts"><span className="text-slate-400">No Patient Outcome:</span> <span className="font-semibold text-slate-700">{journey.no_patient_outcome}</span></div>
          </>
        ) : (
          <div title="Older snapshot: called minus connected, including technical failures"><span className="text-slate-400">Called, Not Connected:</span> <span className="font-semibold text-slate-700">{journey.not_connected}</span></div>
        )}
        <div><span className="text-slate-400">Lost:</span> <span className="font-semibold text-rose-600">{journey.lost}</span></div>
        <div><span className="text-slate-400">Still Open:</span> <span className="font-semibold text-slate-700">{journey.still_open}</span></div>
      </div>
    </div>
  );
}

// ============================================================================
// ATTRIBUTION PERFORMANCE TABLE
// ============================================================================

function AttributionTable({ rows, level }: { rows: AttributionRow[]; level: 'campaign' | 'adset' | 'ad' }) {
  if (!rows || rows.length === 0) {
    return <EmptyState message={`No ${level} attribution data available for this report.`} />;
  }
  const nameKey = level === 'campaign' ? 'campaign_name' : level === 'adset' ? 'adset_name' : 'ad_name';
  const idKey = level === 'campaign' ? 'campaign_id' : level === 'adset' ? 'adset_id' : 'ad_id';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
            <th className="px-2 py-2 font-medium">{level === 'campaign' ? 'Campaign' : level === 'adset' ? 'Ad Set' : 'Ad'}</th>
            <th className="px-2 py-2 text-right font-medium">Leads</th>
            <th className="px-2 py-2 text-right font-medium">Assigned</th>
            <th className="px-2 py-2 text-right font-medium">Called</th>
            <th className="px-2 py-2 text-right font-medium">Conn.</th>
            <th className="px-2 py-2 text-right font-medium" title="Human-confirmed qualification outcome">Positive</th>
            <th className="px-2 py-2 text-right font-medium" title="Current Won status; includes legacy wins without registration">Won (status)</th>
            <th className="px-2 py-2 text-right font-medium" title="Registration recorded with a reference">Reg.</th>
            <th className="px-2 py-2 text-right font-medium" title="Lost, not interested or spam">Lost</th>
            <th className="px-2 py-2 text-right font-medium" title="Connected / called">Conn %</th>
            <th className="px-2 py-2 text-right font-medium" title="Registered / leads">Reg %</th>
            <th className="px-2 py-2 text-right font-medium" title="Lost / leads">Lost %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const name = sourceLabel((row as any)[nameKey] ?? (row as any)[idKey] ?? 'Unknown');
            return (
              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-2 py-2 font-medium text-[#17213A]" title={(row as any)[idKey] ?? ''}>{name}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.total_leads}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.assigned}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.called}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.connected}</td>
                {/* Before schema 12, positive meant "moved to Qualified" — hide it rather than mislabel it. */}
                <td className="px-2 py-2 text-right tabular-nums">{row.registered !== undefined ? (row.positive ?? '—') : '—'}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.won}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold text-emerald-600">{row.registered ?? '—'}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold text-rose-600">{row.lost}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.connection_rate}%</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.registration_rate !== undefined ? `${row.registration_rate}%` : '—'}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.rejection_rate}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function AttributionPerformance({ attribution }: { attribution: AttributionSummary }) {
  const [level, setLevel] = useState<'campaign' | 'adset' | 'ad'>('campaign');
  return (
    <div className="space-y-4" data-export-tabs="attribution">
      <div className="flex gap-1.5">
        {(['campaign', 'adset', 'ad'] as const).map((l) => (
          <button
            key={l}
            onClick={() => setLevel(l)}
            data-export-tab="attribution"
            data-export-value={l}
            className={`rounded-full px-3 py-1 text-[12px] font-medium capitalize ${
              level === l ? 'bg-[#4B49AC] text-white' : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            {l === 'adset' ? 'Ad Set' : l}
          </button>
        ))}
      </div>
      {(['campaign', 'adset', 'ad'] as const).map((l) => (
        <div key={l} data-export-panel="attribution" data-export-value={l} hidden={level !== l}>
          <AttributionTable rows={l === 'campaign' ? attribution.campaigns : l === 'adset' ? attribution.adsets : attribution.ads} level={l} />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// EXECUTIVE INSIGHTS
// ============================================================================

export function ExecutiveInsightsPanel({ insights }: { insights: ExecutiveInsight[] }) {
  if (!insights || insights.length === 0) {
    return <EmptyState message="No executive insights available for this report window." />;
  }
  const insightIcon: Record<string, string> = {
    top_converting_ads: '🏆',
    highest_rejection_ads: '⚠️',
    low_contact_rate: '📞',
    high_connection_low_conversion: '🔍',
    unworked_ads: '📋',
  };
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {insights.map((insight, i) => (
        <div key={i} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-lg">{insightIcon[insight.type] ?? '📊'}</span>
            <h4 className="text-[14px] font-semibold text-[#17213A]">{insight.title}</h4>
          </div>
          <div className="mt-3 space-y-2">
            {insight.details.map((d, j) => (
              <div key={j} className="flex items-center justify-between border-b border-slate-50 pb-1.5 text-[12px] last:border-0">
                <span className="mr-2 truncate text-slate-700" title={sourceLabel(d.label)}>{sourceLabel(d.label)}</span>
                <span className="shrink-0 font-medium text-slate-500">{d.metric_label}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// TODAY'S WORKED LEAD OUTCOMES
// Separate cohort: all unique leads/patients called today, regardless of creation date.
// ============================================================================

function WorkedLeadOutcomeKpis({ outcome }: { outcome: WorkedLeadOutcome }) {
  const ofWorked = (n: number) => (outcome.unique_worked_leads > 0 ? `${pctNum(n, outcome.unique_worked_leads)}% of patients worked` : '');
  const kpis: Array<{ label: string; value: number; sub: string; accent: string; hint?: string }> = [
    { label: 'Unique Patients Worked', value: outcome.unique_worked_leads, sub: `${outcome.total_attempts} call attempts`, accent: PALETTE.indigo },
    { label: 'Patients Connected', value: outcome.connected, sub: ofWorked(outcome.connected), accent: PALETTE.blue },
    { label: 'Patients No Answer', value: outcome.not_connected, sub: ofWorked(outcome.not_connected), accent: PALETTE.textSecondary, hint: 'Technical failures are not counted as no answer' },
    ...(outcome.no_patient_outcome !== undefined ? [{ label: 'No Patient Outcome', value: outcome.no_patient_outcome, sub: 'Only technical failures or unresolved attempts', accent: STATUS_COLORS.spam }] : []),
    ...(outcome.registered_today !== undefined ? [{ label: 'Registrations Today', value: outcome.registered_today, sub: 'Registration reference dated today', accent: STATUS_COLORS.won, hint: 'Not OPD consultations, not payments' }] : []),
    { label: 'Marked Won Today', value: outcome.won_today, sub: 'Status changed to Won today', accent: STATUS_COLORS.won },
    { label: 'Marked Lost Today', value: outcome.lost_today, sub: 'Status changed to lost/not interested/spam today', accent: STATUS_COLORS.lost },
    { label: 'Follow-up Today', value: outcome.follow_up_today, sub: 'Moved to follow-up today', accent: STATUS_COLORS.follow_up },
  ];
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {kpis.map((k) => (
        <SupportKpi key={k.label} label={k.label} value={k.value} sub={k.sub} accent={k.accent} hint={k.hint} />
      ))}
    </div>
  );
}

function WorkedLeadStatusDistribution({ outcome }: { outcome: WorkedLeadOutcome }) {
  const distribution = [
    { label: 'Won (status)', value: outcome.current_won, color: STATUS_COLORS.won },
    { label: 'Lost', value: outcome.current_lost, color: STATUS_COLORS.lost },
    { label: 'Qualified (status)', value: outcome.current_qualified, color: STATUS_COLORS.qualified },
    { label: 'Follow-up', value: outcome.current_follow_up, color: STATUS_COLORS.follow_up },
    { label: 'Open', value: outcome.current_open, color: PALETTE.textSecondary },
  ].filter((d) => d.value > 0);

  if (distribution.length === 0) {
    return <EmptyState message="No worked leads today." />;
  }

  const max = Math.max(...distribution.map((d) => d.value), 1);

  return (
    <div className="space-y-2">
      <div className="mb-2 text-[12px] font-medium text-[#8A9AB5]">Current status of worked leads</div>
      {distribution.map((d) => (
        <div key={d.label} className="flex items-center gap-3">
          <div className="w-24 shrink-0 text-right text-[12px] font-medium text-[#17213A]">{d.label}</div>
          <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
            <div
              className="flex h-full items-center rounded-md px-2 text-[11px] font-semibold text-white"
              style={{ width: `${Math.max((d.value / max) * 100, 2)}%`, backgroundColor: d.color, minWidth: '24px' }}
            >
              {d.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function WorkedLeadReasonBars({
  title,
  reasons,
  total,
  color,
}: {
  title: string;
  reasons: Array<{ reason: string; count: number; percentage: number }>;
  total: number;
  color: string;
}) {
  if (total <= 0 || reasons.length === 0) {
    return (
      <div className="rounded-xl bg-[#F8F9FF] p-4">
        <div className="text-[14px] font-semibold text-[#17213A]">{title}</div>
        <div className="mt-2 text-[12px] text-[#8A9AB5]">No {title.toLowerCase()} recorded today.</div>
      </div>
    );
  }
  const maxReason = Math.max(...reasons.map((r) => r.count), 1);
  return (
    <div className="rounded-xl bg-[#F8F9FF] p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[14px] font-semibold text-[#17213A]">{title}</span>
        <span className="text-[13px] font-medium text-[#8A9AB5]">{total} leads</span>
      </div>
      <div className="space-y-2.5">
        {reasons.slice(0, 8).map((r) => (
          <div key={r.reason}>
            <div className="mb-1 flex items-center justify-between text-[12px]">
              <span className="truncate font-medium text-[#17213A]" title={r.reason}>{r.reason}</span>
              <span className="ml-3 shrink-0 font-semibold text-[#17213A]">
                {r.count} <span className="font-normal text-[#8A9AB5]">· {r.percentage}%</span>
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-white">
              <div
                className="h-2 rounded-full"
                style={{ width: `${(r.count / maxReason) * 100}%`, backgroundColor: color }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkedLeadWorkTypeTable({ breakdown }: { breakdown: WorkedLeadOutcome['work_type_breakdown'] }) {
  if (!breakdown || breakdown.length === 0) {
    return <EmptyState message="No work-type breakdown available." />;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
            <th className="px-2 py-2 font-medium">Work Type</th>
            <th className="px-2 py-2 text-right font-medium">Attempts</th>
            <th className="px-2 py-2 text-right font-medium">Unique</th>
            <th className="px-2 py-2 text-right font-medium">Conn.</th>
            <th className="px-2 py-2 text-right font-medium" title="Patient-side no answer; technical failures excluded">No Ans.</th>
            <th className="px-2 py-2 text-right font-medium" title="Status changed to Won today">Marked Won</th>
            <th className="px-2 py-2 text-right font-medium" title="Status changed to lost/not interested/spam today">Marked Lost</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.map((row) => (
            <tr key={row.type} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="px-2 py-2 font-medium text-[#17213A]">{row.label}</td>
              <td className="px-2 py-2 text-right tabular-nums">{row.attempts}</td>
              <td className="px-2 py-2 text-right tabular-nums">{row.unique}</td>
              <td className="px-2 py-2 text-right tabular-nums">{row.connected}</td>
              <td className="px-2 py-2 text-right tabular-nums">{row.not_connected}</td>
              <td className="px-2 py-2 text-right tabular-nums font-semibold text-emerald-600">{row.won}</td>
              <td className="px-2 py-2 text-right tabular-nums font-semibold text-rose-600">{row.lost}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkedAttributionTable({ rows, level }: { rows: WorkedAttributionRow[]; level: 'campaign' | 'adset' | 'ad' }) {
  if (!rows || rows.length === 0) {
    return <EmptyState message={`No ${level} attribution data for worked leads.`} />;
  }
  const nameKey = level === 'campaign' ? 'campaign_name' : level === 'adset' ? 'adset_name' : 'ad_name';
  const idKey = level === 'campaign' ? 'campaign_id' : level === 'adset' ? 'adset_id' : 'ad_id';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
            <th className="px-2 py-2 font-medium">{level === 'campaign' ? 'Campaign' : level === 'adset' ? 'Ad Set' : 'Ad'}</th>
            <th className="px-2 py-2 text-right font-medium">Worked</th>
            <th className="px-2 py-2 text-right font-medium">Conn.</th>
            <th className="px-2 py-2 text-right font-medium" title="Patient-side no answer; technical failures excluded">No Ans.</th>
            <th className="px-2 py-2 text-right font-medium" title="Current Won status; includes legacy wins without registration">Won (status)</th>
            <th className="px-2 py-2 text-right font-medium" title="Registration recorded with a reference">Reg.</th>
            <th className="px-2 py-2 text-right font-medium">Lost</th>
            <th className="px-2 py-2 text-right font-medium">FU</th>
            <th className="px-2 py-2 text-right font-medium">Open</th>
            <th className="px-2 py-2 text-right font-medium" title="Connected / worked">Conn %</th>
            <th className="px-2 py-2 text-right font-medium" title="Registered / worked">Reg %</th>
            <th className="px-2 py-2 text-right font-medium" title="Lost / worked">Lost %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const name = (row as any)[nameKey] ?? (row as any)[idKey] ?? 'Unknown';
            return (
              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-2 py-2 font-medium text-[#17213A]" title={(row as any)[idKey] ?? ''}>{name}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.worked_leads}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.connected}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.not_connected}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.won}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold text-emerald-600">{row.registered ?? '—'}</td>
                <td className="px-2 py-2 text-right tabular-nums font-semibold text-rose-600">{row.lost}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.follow_up}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.open}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.connection_rate}%</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.registration_rate !== undefined ? `${row.registration_rate}%` : '—'}</td>
                <td className="px-2 py-2 text-right tabular-nums">{row.rejection_rate}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WorkedAttributionPerformance({ attribution }: { attribution: WorkedLeadOutcomes['attribution'] }) {
  const [level, setLevel] = useState<'campaign' | 'adset' | 'ad'>('campaign');
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">
        {(['campaign', 'adset', 'ad'] as const).map((l) => (
          <button
            key={l}
            onClick={() => setLevel(l)}
            data-export-tab="worked-attribution"
            data-export-value={l}
            className={`rounded-full px-3 py-1 text-[12px] font-medium capitalize ${
              level === l ? 'bg-[#4B49AC] text-white' : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            {l === 'adset' ? 'Ad Set' : l}
          </button>
        ))}
      </div>
      {(['campaign', 'adset', 'ad'] as const).map((l) => (
        <div key={l} data-export-panel="worked-attribution" data-export-value={l} hidden={level !== l}>
          <WorkedAttributionTable rows={l === 'campaign' ? attribution.campaigns : l === 'adset' ? attribution.adsets : attribution.ads} level={l} />
        </div>
      ))}
    </div>
  );
}

export function ExportableReasonExplorers({
  selectedStatus,
  pipeline,
  pipelineTotal,
  statusReasonBreakdown,
}: {
  selectedStatus: string;
  pipeline: PipelineEntry[];
  pipelineTotal: number;
  statusReasonBreakdown: StatusReasonEntry[];
}) {
  const statuses = pipeline.filter((entry) => entry.count > 0).map((entry) => entry.status);
  return (
    <div data-export-panels="status">
      {statuses.map((status) => (
        <div key={status} data-export-panel="status" data-export-value={status} hidden={selectedStatus !== status}>
          <ReasonExplorer
            selectedStatus={status}
            pipeline={pipeline}
            pipelineTotal={pipelineTotal}
            statusReasonBreakdown={statusReasonBreakdown}
          />
        </div>
      ))}
    </div>
  );
}

function WorkedLeadOutcomesSection({ data }: { data: WorkedLeadOutcomes }) {
  const { outcome, attribution, insights } = data;
  if (outcome.unique_worked_leads === 0) {
    return (
      <Card>
        <SectionLabel
          title="Today's Worked Lead Outcomes"
          subtitle="Unique patients on any CRM-visible call today (inbound or outbound), regardless of when the lead was created"
        />
        <EmptyState message="No leads were worked today." />
      </Card>
    );
  }
  return (
    <>
      {/* KPIs */}
      <Card>
        <SectionLabel
          title="Today's Worked Lead Outcomes"
          subtitle="Unique patients on any CRM-visible call today (inbound or outbound), regardless of when the lead was created"
          right={<span className="text-[13px] font-medium text-[#8A9AB5]">{outcome.unique_worked_leads} unique · {outcome.total_attempts} attempts</span>}
        />
        <WorkedLeadOutcomeKpis outcome={outcome} />
      </Card>

      {/* Status distribution + work-type table */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <SectionLabel title="Worked Lead Status Distribution" subtitle="Current status of all leads worked today" />
          <WorkedLeadStatusDistribution outcome={outcome} />
        </Card>
        <Card>
          <SectionLabel title="Work-Type Breakdown" subtitle="New vs old vs follow-up work performed today" />
          <WorkedLeadWorkTypeTable breakdown={outcome.work_type_breakdown} />
        </Card>
      </div>

      {/* Reason analysis */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <WorkedLeadReasonBars
          title="Marked Lost Today — Reasons"
          reasons={outcome.lost_reasons_today}
          total={outcome.lost_total_today}
          color={STATUS_COLORS.lost}
        />
        <WorkedLeadReasonBars
          title="Marked Won Today — Reasons"
          reasons={outcome.won_reasons_today}
          total={outcome.won_total_today}
          color={STATUS_COLORS.won}
        />
      </div>

      {/* Attribution */}
      {attribution.campaigns.length > 0 && (
        <Card>
          <SectionLabel
            title="Worked Lead Attribution"
            subtitle="Source → Campaign → Ad Set → Ad for leads worked today"
          />
          <WorkedAttributionPerformance attribution={attribution} />
        </Card>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <div className="space-y-4">
          <div className="px-1">
            <h3 className="text-[17px] font-semibold tracking-tight text-[#17213A]">Worked Lead Insights</h3>
            <p className="mt-0.5 text-[13px] text-[#8A9AB5]">Deterministic findings from today's worked cohort</p>
          </div>
          <ExecutiveInsightsPanel insights={insights} />
        </div>
      )}
    </>
  );
}

// ============================================================================
// MAIN REPORT
// ============================================================================

type LookForward = { positive: number; booked: number; undecided: number; warm_in_hand: number; followups_due: number; kind_comments: number; kind_quote: string | null };

export default function EodExecutiveReport({ report }: { report: AdminAnalyticsReport }) {
  const summary = (report.overall_summary ?? {}) as EodSummary;
  const charts = (report.chart_data ?? {}) as EodChartData;
  const agents = (report.telecaller_summary ?? []) as unknown as AgentRow[];

  const pipeline = charts.pipeline ?? [];
  const pipelineTotal = num(charts.pipeline_total ?? summary.new_today ?? 0);
  const statusReasonBreakdown = charts.status_reason_breakdown ?? [];
  const workType = charts.work_type ?? [];
  const callsByAgent = charts.calls_by_telecaller ?? [];
  const agentWorkMix = charts.agent_work_mix ?? [];
  const agentOutcomes = charts.agent_outcomes ?? [];
  const journey = charts.journey_summary;
  const attribution = charts.attribution_summary;
  const insights = charts.executive_insights;
  const workedLeadOutcomes = (charts as any).worked_lead_outcomes as WorkedLeadOutcomes | undefined;
  const connected = num(summary.connected_calls);
  const notConnected = num(summary.not_connected_calls);
  const totalCalls = num(summary.total_call_attempts);
  const uniqueCalled = num(summary.unique_leads_called);
  // Undefined on snapshots saved before schema 12, which silently dropped these patients.
  const noPatientOutcome = summary.no_patient_outcome;
  const technicalFailures = summary.technical_failure_attempts;
  const newToday = num(summary.new_today);
  const totalUnassigned = num(summary.total_unassigned);
  const unassignedToday = num(summary.unassigned_today);
  const lostCount = pipeline.find((p) => p.status === 'lost')?.count ?? 0;
  const lostRate = pctNum(lostCount, pipelineTotal);
  const connRate = uniqueCalled > 0 ? Math.round((connected / uniqueCalled) * 1000) / 10 : 0;
  const assignedRate = newToday > 0 ? pctNum(newToday - unassignedToday, newToday) : 0;

  // Default selection: Lost if > 0, else first reason-eligible status with count > 0, else first outcome.
  const [selectedStatus, setSelectedStatus] = useState<string>(() => {
    if (lostCount > 0) return 'lost';
    for (const s of STATUSES_WITH_REASONS) {
      const found = pipeline.find((p) => p.status === s && p.count > 0);
      if (found) return s;
    }
    const firstOutcome = pipeline.find((p) => p.status !== 'unassigned' && p.count > 0);
    return firstOutcome?.status ?? 'lost';
  });

  const ahead = (charts as any).look_forward as LookForward | undefined;

  return (
    <div className="space-y-6 px-6 py-6 pb-10">
      {ahead && (
        <Card>
          <SectionLabel title="To look forward to" subtitle="What today's work built up. Registrations take days; these are the patients on the way." />
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
            <SupportKpi label="Positive today" value={ahead.positive} sub="likely to convert" accent={STATUS_COLORS.qualified} />
            <SupportKpi label="Booked today" value={ahead.booked} sub="appointment requested" accent={STATUS_COLORS.won} />
            <SupportKpi label="Undecided today" value={ahead.undecided} sub="needs information or time" accent={PALETTE.blue} />
            <SupportKpi label="Warm patients in hand" value={ahead.warm_in_hand} sub="Positive, Booked or Undecided, still open" accent={PALETTE.indigo} />
            <SupportKpi label="Follow-ups tomorrow" value={ahead.followups_due} sub="already scheduled" accent={PALETTE.violet} />
          </div>
          {ahead.kind_comments > 0 && (
            <p className="mt-3 text-sm text-slate-600">
              Kind words this week: {ahead.kind_comments} on Facebook/Instagram{ahead.kind_quote ? <> — <i>"{ahead.kind_quote}"</i></> : null}
            </p>
          )}
        </Card>
      )}
      {/* ===== KPI AREA — hero + supporting ===== */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {/* Hero: New Leads Today */}
        <HeroKpi
          label="New Lead Records Today"
          value={newToday}
          sub={`${newToday - unassignedToday} assigned · ${assignedRate}% · incl. every inbound call ring`}
          barValue={assignedRate}
          barColor="#ffffff"
        />
        {/* Supporting KPIs */}
        <SupportKpi
          label="Call Attempts"
          value={totalCalls}
          sub={technicalFailures !== undefined ? `${uniqueCalled} unique patients · ${technicalFailures} technical failures` : `${uniqueCalled} unique patients`}
          accent={PALETTE.indigo}
          hint="Inbound and outbound CRM-visible attempts, including technical failures and unresolved attempts"
        />
        <SupportKpi
          label="Patient Connect Rate"
          value={`${connRate}%`}
          sub={`${connected} of ${uniqueCalled} patients answered`}
          accent={PALETTE.blue}
          hint="Unique patients with an answered call ÷ unique patients attempted"
        />
        <SupportKpi label="Lost Leads" value={lostCount} sub={`${lostRate}% of today's lead records (status Lost)`} accent={STATUS_COLORS.lost} />
      </div>

      {/* Secondary KPI row — Unique Called + Unassigned distinction */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SupportKpi
          label="Unique Patients on Calls"
          value={uniqueCalled}
          sub={noPatientOutcome !== undefined ? `${noPatientOutcome} with no patient outcome (technical failure / unresolved)` : `${totalCalls} call attempts`}
          accent={PALETTE.violet}
          hint="Inbound and outbound; phone-first unique patients"
        />
        <SupportKpi label="Today's Unassigned Lead Records" value={unassignedToday} sub={`of ${newToday} lead records today`} accent={STATUS_COLORS.unassigned} />
        <SupportKpi label="Overall Unassigned Queue" value={totalUnassigned} sub="new/pending lead records with no assignee, any date" accent={PALETTE.textSecondary} />
      </div>

      {/* ===== LEAD JOURNEY + REASON EXPLORER (centerpiece) ===== */}
      <Card>
        <SectionLabel
          title="Today's Lead Journey"
          subtitle="Lead records created today (incl. inbound call rings) — click a status to see why"
          right={<span className="text-[13px] font-medium text-[#8A9AB5]">{pipelineTotal} leads</span>}
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
          <LeadJourney
            pipeline={pipeline}
            pipelineTotal={pipelineTotal}
            selected={selectedStatus}
            onSelect={setSelectedStatus}
          />
          <ExportableReasonExplorers
            selectedStatus={selectedStatus}
            pipeline={pipeline}
            pipelineTotal={pipelineTotal}
            statusReasonBreakdown={statusReasonBreakdown}
          />
        </div>
      </Card>

      {/* ===== LEAD JOURNEY FUNNEL (schema v3) ===== */}
      {journey && journey.total_leads > 0 && (
        <Card>
          <SectionLabel
            title="Lead Journey Funnel"
            subtitle="Total → Assigned → Called → Connected → Marked Qualified → Positive → Won (status) → Registered"
            right={<span className="text-[13px] font-medium text-[#8A9AB5]">{journey.total_leads} people</span>}
          />
          <JourneyFunnel journey={journey} />
        </Card>
      )}

      {/* ===== ATTRIBUTION PERFORMANCE (schema v3) ===== */}
      {attribution && (attribution.campaigns.length > 0 || attribution.adsets.length > 0 || attribution.ads.length > 0) && (
        <Card>
          <SectionLabel
            title="Attribution Performance"
            subtitle="Today's Meta-attributed lead records by Campaign → Ad Set → Ad: connect, registration, and lost rates"
          />
          <AttributionPerformance attribution={attribution} />
        </Card>
      )}

      {/* ===== EXECUTIVE INSIGHTS (schema v3) ===== */}
      {insights && insights.length > 0 && (
        <div className="space-y-4">
          <div className="px-1">
            <h3 className="text-[17px] font-semibold tracking-tight text-[#17213A]">Executive Insights</h3>
            <p className="mt-0.5 text-[13px] text-[#8A9AB5]">Automated findings from today's attribution data</p>
          </div>
          <ExecutiveInsightsPanel insights={insights} />
        </div>
      )}

      {/* ===== CALLING PERFORMANCE ===== */}
      <Card>
        <SectionLabel title="Calling Performance" subtitle="Call attempts (inbound + outbound) and unique-patient outcomes" />
        <CallingPerformance
          totalCalls={totalCalls}
          uniqueCalled={uniqueCalled}
          connected={connected}
          notConnected={notConnected}
          noPatientOutcome={noPatientOutcome}
          workType={workType}
        />
      </Card>

      {/* ===== TODAY'S WORKED LEAD OUTCOMES ===== */}
      {workedLeadOutcomes && <WorkedLeadOutcomesSection data={workedLeadOutcomes} />}

      {/* ===== AGENT PERFORMANCE ===== */}
      <div className="space-y-4">
        <div className="px-1">
          <h3 className="text-[17px] font-semibold tracking-tight text-[#17213A]">Agent Performance</h3>
          <p className="mt-0.5 text-[13px] text-[#8A9AB5]">Comparison and detailed breakdown</p>
        </div>
        <Card>
          <AgentComparisonChart callsByAgent={callsByAgent} agentWorkMix={agentWorkMix} />
        </Card>
        <Card padding="p-4">
          <AgentTable rows={agents} outcomes={agentOutcomes} />
        </Card>
      </div>
    </div>
  );
}
